import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHeader from "@/shared/components/layout/dashboard-layout/page-header.tsx";
import PageContent from "@/shared/components/layout/dashboard-layout/page-content.tsx";
import MachineSettingsForm from "@/machines/components/machine-settings-form/machine-settings-form.tsx";
import { useGetMachineById } from "@/machines/api/queries/machines.queries.ts";
import SkeletonPage from "@/shared/components/skeleton/page/skeleton-page.tsx";
import EmptyState from "@/shared/components/ui/empty-state/empty-state.tsx";

const MachineSettings = () => {
  const { t } = useTranslation("machines");
  const [searchParams] = useSearchParams();
  const machineIdParam = searchParams.get("scope");

  // Extract machine ID from scope parameter (e.g., "machine" or "123")
  const machineId = machineIdParam && machineIdParam !== "machine" ? parseInt(machineIdParam, 10) : null;

  const { data: machine, isLoading, isError } = useGetMachineById(machineId || 0);

  useEffect(() => {
    if (!machineIdParam) {
      console.warn("No scope parameter found in URL");
    }
  }, [machineIdParam]);

  if (!machineIdParam || machineIdParam === "machine") {
    return (
      <>
        <PageHeader
          breadcrumbs={[
            {
              path: "/settings",
              breadcrumb: t("breadcrumbs.settings"),
            },
          ]}
        />
        <PageContent>
          <EmptyState
            title={t("settingsPage.noSelection.title")}
            description={t("settingsPage.noSelection.description")}
          />
        </PageContent>
      </>
    );
  }

  if (isLoading) {
    return <SkeletonPage />;
  }

  if (isError || !machine?.data) {
    return (
      <>
        <PageHeader
          breadcrumbs={[
            {
              path: "/settings",
              breadcrumb: t("breadcrumbs.settings"),
            },
          ]}
        />
        <PageContent>
          <EmptyState
            title={t("settingsPage.notFound.title")}
            description={t("settingsPage.notFound.description", { id: machineId })}
          />
        </PageContent>
      </>
    );
  }

  return (
    <>
      <PageHeader
        breadcrumbs={[
          {
            path: "/settings",
            breadcrumb: t("breadcrumbs.settings"),
          },
          {
            path: `/settings?scope=${machineId}`,
            breadcrumb: machine.data.name || t("breadcrumbs.machine", { id: machineId }),
          },
        ]}
      />
      <PageContent>
        <div className="max-w-3xl">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold">{t("settingsPage.title")}</h1>
            <p className="text-muted-foreground mt-1">
              {t("settingsPage.description", { name: machine.data.name })}
            </p>
          </div>

          <MachineSettingsForm machine={machine.data} />
        </div>
      </PageContent>
    </>
  );
};

export default MachineSettings;
